import { translateBackend } from '@assets/i18n/config';
import type { CustomField } from '@interfaces/customField';
import { Box } from '@ovComponents/1-primative/box/box';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { useHelpTextTokens } from '@ovComponents/2-component/helpText/helpText.tokens';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { useCustomFieldRenderer } from '@ovComponents/3-pattern/customField/useCustomFieldRenderer';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { buildEnrichedCustomOptions, invalidFields } from '../utils';
import { HouseholdSelection } from './components/householdSelection';
import { ObjectTypes } from './components/householdSelection.constants';

export const EditGoalVisual = ({
  options,
  goalData,
  loading,
  updateGoal,
  continueFunc,
  grid = false,
  updateMode = false,
  activeCustomFields,
  derivedCustomFields,
  workflowCompletion,
  ignoreCustomFieldTriggerRules,
  userId,
}: {
  options: any;
  goalData: any;
  loading: boolean;
  updateGoal: any;
  continueFunc: any;
  grid?: boolean;
  updateMode?: boolean;
  activeCustomFields?: string[];
  derivedCustomFields?: CustomField[];
  workflowCompletion?: any;
  ignoreCustomFieldTriggerRules?: boolean;
  userId?: string;
}) => {
  const { sys } = useThemeTokens();
  const { comp: helpTextComp } = useThemeTokens(useHelpTextTokens());
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { t } = useTranslation(['client', 'shared']);
  const [focused, setFocused] = useState<string[]>([]);
  const [updated, setUpdated] = useState<boolean>(false);
  const { activeOrganization } = useContext(UserContext);
  const [customOptionSelected, setCustomOptionSelected] = useState<any[]>([]);
  const { renderCustomFields, validateAllCustomFields } =
    useCustomFieldRenderer({
      customOptions: customOptionSelected,
      customFieldValues: goalData.customFields,
    });

  const shouldDisplayInDialog = helpTextComp.helpText.displayInDialog;

  const submit = async () => {
    const fields = invalidFields(
      {
        ...options,
      },
      goalData,
    );

    const invalidCustomFieldKeys = validateAllCustomFields();
    fields.push(...invalidCustomFieldKeys);
    setFocused(fields);
    if (fields.length === 0) {
      sendAnalytic(ovAnalyticsEvents.workflowsEditGoalContinueButtonSelect, {
        workflowStepTitle: options?.title,
        workflowId: workflowCompletion?.workflow?.id,
        workflowName: workflowCompletion?.workflow?.name,
        activeWorkflowCompletionId: workflowCompletion?.id,
        objectId: workflowCompletion?.objectId,
        objectType: workflowCompletion?.objectType,
      });
      await continueFunc();
    }
  };

  const update = (newValue: any) => {
    setUpdated(true);
    updateGoal(newValue);
  };

  useEffect(() => {
    setCustomOptionSelected(
      buildEnrichedCustomOptions(
        options,
        activeCustomFields,
        derivedCustomFields,
      ),
    );
  }, [options, activeCustomFields, derivedCustomFields]);

  const enableHousehold: boolean =
    activeOrganization.availableFeatureFlags?.includes('HOUSEHOLD') ?? false;
  const defaultLinkToHousehold =
    (enableHousehold && options?.defaultLinkToHousehold) ?? false;

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      testId='edit-goal-form'
      submitButtonProps={{
        label: t(updateMode ? 'update' : 'continue'),
        disabled: loading || (!updated && updateMode),
        dataTestId: `edit-goal-${kebabCase(translateBackend(options.title))}-continue-button`,
      }}
    >
      {options?.title && (
        <Typography variant='displayLarge' sx={{ mt: 1, mb: 3 }}>
          {translateBackend(options?.title)}
          <Box display='inline-flex'>
            {additionalInfo(options?.title?.additionalInfo) && (
              <InfoDialog
                information={
                  additionalInfo(options?.title?.additionalInfo) ?? ''
                }
              />
            )}
          </Box>
        </Typography>
      )}
      {options?.subtitle && (
        <Typography
          display='flex'
          alignItems='end'
          variant='bodyLarge'
          component={'div'}
          sx={{ mb: 3, table: { width: '100%' } }}
        >
          <Box
            display='flex'
            gap={2}
            flexDirection='column'
            sx={{ m: 0, '& > *': { m: 0 } }}
          >
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackend(options?.subtitle)}
            </ReactMarkdown>
          </Box>
          {additionalInfo(options?.subtitle?.additionalInfo) && (
            <InfoDialog
              information={
                additionalInfo(options?.subtitle?.additionalInfo) ?? ''
              }
            />
          )}
        </Typography>
      )}
      <Grid container spacing={2}>
        {options?.name?.enabled && (
          <Grid item xs={12} md={grid ? 4 : 12}>
            <TextField
              testId='goal-name'
              onChange={(e: any) =>
                update({ ...goalData, name: e.target.value })
              }
              disabled={loading}
              label={translateBackend(options?.name?.label)}
              infoTooltip={additionalInfo(options?.name?.additionalInfo)}
              fullWidth
              sx={{ mr: 2 }}
              value={goalData.name}
              onBlur={() => setFocused([...focused, 'name'])}
              error={
                !goalData.name &&
                focused.includes('name') &&
                options?.name?.required !== FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.targetAmountCents?.enabled && (
          <Grid item xs={12} md={grid ? 4 : 12}>
            <TextField
              testId='goal-target-amount'
              type='dollar'
              onChange={(e: any) =>
                update({ ...goalData, targetAmountCents: e.target.valueCents })
              }
              disabled={loading}
              label={translateBackend(options?.targetAmountCents?.label)}
              infoTooltip={additionalInfo(
                options?.targetAmountCents?.additionalInfo,
              )}
              fullWidth
              sx={{ mt: 2, mr: 2 }}
              value={goalData.targetAmountCents / 100}
              leadingIcon='dollar'
              onBlur={() => setFocused([...focused, 'targetAmountCents'])}
              error={
                !goalData.targetAmountCents &&
                focused.includes('targetAmountCents') &&
                options?.targetAmountCents?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.timeHorizon?.enabled && options?.options ? (
          <Grid item xs={12}>
            <Typography
              variant='labelSmall'
              sx={{ color: sys.color.onSurfaceVariant, mb: 1, mt: 2 }}
            >
              {translateBackend(options?.timeHorizon?.label)}
              {additionalInfo(options?.timeHorizon?.additionalInfo) &&
                shouldDisplayInDialog && (
                  <InfoDialog
                    information={
                      additionalInfo(options?.timeHorizon?.additionalInfo) ?? ''
                    }
                  />
                )}
            </Typography>
            <SelectionTile
              testId='goal-time-horizon'
              onChange={(e: any) =>
                updateGoal({ ...goalData, timeHorizon: e.target.value })
              }
              value={goalData.timeHorizon}
              options={options?.options?.map((option: any) => ({
                value: option.value,
                label: translateBackend(option.label),
              }))}
              error={
                !goalData.timeHorizon &&
                focused.includes('timeHorizon') &&
                options?.timeHorizon?.required
              }
              infoTooltip={
                additionalInfo(options?.timeHorizon?.additionalInfo) &&
                !shouldDisplayInDialog
                  ? additionalInfo(options?.timeHorizon?.additionalInfo)
                  : undefined
              }
            />
          </Grid>
        ) : (
          options?.timeHorizon?.enabled &&
          !options?.options && (
            <Grid item xs={12}>
              <Typography
                variant='labelSmall'
                sx={{ color: sys.color.onSurfaceVariant, mb: 1, mt: 2 }}
              >
                {t('client:timeHorizon')}
              </Typography>
              <SelectionTile
                testId='goal-time-horizon'
                onChange={(e: any) =>
                  updateGoal({ ...goalData, timeHorizon: e.target.value })
                }
                value={goalData.timeHorizon}
                options={[
                  {
                    value: 'VERY_SHORT_TERM',
                    label: t('timeHorizonOptions.VERY_SHORT_TERM'),
                  },
                  {
                    value: 'SHORT_TERM',
                    label: t('timeHorizonOptions.SHORT_TERM'),
                  },
                  {
                    value: 'MID_TERM',
                    label: t('timeHorizonOptions.MID_TERM'),
                  },
                  {
                    value: 'LONG_TERM',
                    label: t('timeHorizonOptions.LONG_TERM'),
                  },
                ]}
                error={
                  !goalData.timeHorizon &&
                  focused.includes('timeHorizon') &&
                  options?.timeHorizon?.required !==
                    FieldRequiredType.NOT_REQUIRED
                }
                errorText={t('components:fieldErrors.selectionTile')}
              />
            </Grid>
          )
        )}
        {defaultLinkToHousehold && (
          <Grid
            item
            xs={12}
            md={12}
            sx={{
              mb: 1,
              mt: 2,
              mr: 2,
            }}
          >
            <HouseholdSelection
              data-testid='house-hold-select'
              data={goalData}
              infoTooltip={additionalInfo(
                options?.defaultLinkToHousehold?.additionalInfo,
              )}
              updateData={updateGoal}
              objectType={ObjectTypes.GOAL}
            />
          </Grid>
        )}
        {customOptionSelected && (
          <Grid item xs={12}>
            {renderCustomFields({
              customData: goalData,
              update,
              grid,
              focused,
              setFocused,
              loading,
              ignoreCustomFieldTriggerRules,
              userId: userId ?? '',
            })}
          </Grid>
        )}
      </Grid>
    </WorkflowCompletionForm>
  );
};

export default EditGoalVisual;
